import type { Block } from "building-types/blocks/blockTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Table } from "common/data/table";
import type { AABB3 } from "geom/aabb3Types";
import { levelBelowId } from "../level/levelTools";

// Ignore shallow eave overlap so small pieces do not hover at the edge of the dollhouse view.
const ROOF_LEVEL_OVERLAP_TOLERANCE = 2;

/**
 * Creates a resolver for the level group that should own roof geometry in interior views.
 * Roofs extending into a half-story belong to the level below so they remain visible while
 * the selected half-story is clipped.
 */
export function createRoofRenderLevelResolver(
  levels: Table<Level>,
  blocks: Table<Block>,
): (roofAabb: AABB3) => string | undefined {
  const halfStoryLevelIds = new Set(
    Object.values(blocks)
      .filter((block) => block.isHalfStory)
      .map((block) => block.levelId),
  );

  return (roofAabb: AABB3): string | undefined => {
    let lowestLevelCovered: Level | undefined;
    for (const level of Object.values(levels)) {
      if (
        level.floorElevation + level.topOfWall - ROOF_LEVEL_OVERLAP_TOLERANCE > roofAabb.minZ &&
        (lowestLevelCovered === undefined || level.floorElevation < lowestLevelCovered.floorElevation) &&
        halfStoryLevelIds.has(level.id)
      ) {
        lowestLevelCovered = level;
      }
    }

    return lowestLevelCovered ? levelBelowId(lowestLevelCovered.id, levels) : undefined;
  };
}
